import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { UserContext } from '../App';
import GatherFormPage from '../components/common/GatherFormPage';
import { supabase } from '../utils/supabaseClient';
import { awardPoints } from '../utils/pointsService';
import { useModal } from '../context/ModalContext';
import CountrySelector from '../components/travel/CountrySelector';
import TravelImagePicker from '../components/travel/TravelImagePicker';
import TravelPostCard from '../components/travel/TravelPostCard';
import { uploadTravelImages } from '../utils/travelImages';
import { TRAVEL_BUDGET_OPTIONS, TRAVEL_GENDER_OPTIONS } from '../utils/travelPostLabels';

const DRAFT_KEY = 'joinu_create_travel_draft';
const EMPTY_FORM = {
  destination: '',
  countries: [],
  trip_start_date: '',
  trip_end_date: '',
  budget_range: '',
  max_companions: 2,
  gender_pref: 'any',
  description: ''
};

const loadDraft = () => {
  try {
    const draft = window.localStorage.getItem(DRAFT_KEY);
    return draft ? { ...EMPTY_FORM, ...JSON.parse(draft) } : EMPTY_FORM;
  } catch {
    return EMPTY_FORM;
  }
};

const inputStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '48px',
  padding: '12px 14px',
  color: '#0f172a',
  background: '#f8fafc',
  border: '1px solid #dbe4ee',
  borderRadius: '14px',
  fontFamily: 'inherit',
  fontSize: '16px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#334155',
  fontSize: '13px',
  fontWeight: '750'
};

function ChipGroup({ options, selected, onChange, label }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {options.map(option => {
        const isSelected = selected === option.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isSelected}
            onClick={() => onChange(option.value)}
            style={{
              minHeight: '44px',
              padding: '9px 15px',
              color: isSelected ? '#ffffff' : '#475569',
              background: isSelected ? '#4e8bca' : '#ffffff',
              border: isSelected ? '1px solid #4e8bca' : '1px solid #dbe4ee',
              borderRadius: '999px',
              boxShadow: isSelected ? '0 5px 14px rgba(78,139,202,0.22)' : 'none',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default function CreateTravelPost() {
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { showAlert } = useModal();
  const [isSaving, setIsSaving] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [images, setImages] = useState([]);
  const [form, setForm] = useState(loadDraft);
  const [errors, setErrors] = useState({});
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
        setDraftSavedAt(new Date());
      } catch {
        // Storage may be unavailable; the page remains usable.
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [form]);

  const previewPost = useMemo(() => ({
    id: 'preview',
    destination: form.destination.trim() || '你的旅程標題',
    countries: form.countries,
    trip_start_date: form.trip_start_date,
    trip_end_date: form.trip_end_date,
    budget_range: form.budget_range,
    max_companions: form.max_companions,
    gender_pref: form.gender_pref,
    description: form.description.trim() || '補充旅行計畫與期待的旅伴特質，讓大家更了解這趟旅程。',
    image_urls: images.map(item => item.url),
    profiles: currentUser?.profile || {},
    travel_applications: []
  }), [currentUser?.profile, form, images]);

  const updateField = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setErrors(previous => ({ ...previous, [field]: undefined }));
  };

  const showErrors = nextErrors => {
    setErrors(nextErrors);
    const firstField = Object.keys(nextErrors)[0];
    window.requestAnimationFrame(() => {
      const field = document.getElementById(`travel-form-${firstField}`);
      field?.focus();
      field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.destination.trim()) nextErrors.destination = '請填寫目的地標題';
    if (form.countries.length === 0) nextErrors.countries = '請至少選擇一個國家';
    if (form.trip_start_date && form.trip_end_date && form.trip_end_date < form.trip_start_date) nextErrors.trip_end_date = '回程日不能早於出發日';
    if (Object.keys(nextErrors).length > 0) {
      showErrors(nextErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSubmit = async () => {
    if (!currentUser || !validateForm()) return;
    setIsSaving(true);
    try {
      const imageUrls = await uploadTravelImages(currentUser.id, images.map(item => item.file));
      const { data: newPost, error } = await supabase
        .from('travel_posts')
        .insert({
          organizer_id: currentUser.id,
          image_urls: imageUrls,
          destination: form.destination.trim(),
          country: form.countries[0],
          countries: form.countries,
          trip_start_date: form.trip_start_date || null,
          trip_end_date: form.trip_end_date || null,
          budget_range: form.budget_range || null,
          max_companions: form.max_companions,
          gender_pref: form.gender_pref,
          description: form.description.trim() || null
        })
        .select('id')
        .single();
      if (error) throw error;
      window.localStorage.removeItem(DRAFT_KEY);
      showAlert('成功', '揪旅伴貼文已發布！');
      awardPoints('create_travel_post', newPost.id);
      navigate(`/travel/${newPost.id}`);
    } catch (error) {
      console.error('handleSubmit error:', error);
      showAlert('錯誤', '發布失敗，請稍後再試');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = () => {
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {
      // Leaving remains available when browser storage is unavailable.
    }
    navigate('/travel');
  };

  const ErrorText = ({ field }) => errors[field] ? <p role="alert" style={{ margin: '6px 0 0', color: '#dc2626', fontSize: '12px', fontWeight: '650' }}>{errors[field]}</p> : null;
  const errorBorder = field => errors[field] ? '#ef4444' : '#dbe4ee';

  return (
    <GatherFormPage
      title="發起揪旅伴"
      subtitle="一次填完旅行計畫，隨時查看發布預覽"
      backLabel="返回旅伴列表"
      onBack={() => navigate('/travel')}
      preview={<TravelPostCard post={previewPost} isPreview />}
      previewTitle="發布預覽"
      primaryLabel="發布揪旅伴"
      savingLabel="發布中..."
      onPrimary={handleSubmit}
      draftStatus={`文字草稿已儲存於此裝置${draftSavedAt ? ` · ${draftSavedAt.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}` : ''} · 照片尚未保存`}
      onSaveDraft={handleSaveDraft}
      isSaving={isSaving}
    >
      <section className="gather-form-section" aria-labelledby="travel-basic-heading">
        <div className="gather-form-section-header">
          <h2 id="travel-basic-heading">基本資訊</h2>
          <p>先說明目的地與旅行地區，讓旅伴快速理解行程輪廓。</p>
        </div>
        <div className="gather-form-fields">
          <div>
            <label htmlFor="travel-form-destination" style={labelStyle}>目的地標題 *</label>
            <div style={{ position: 'relative' }}>
              <MapPin size={17} color="#94a3b8" style={{ position: 'absolute', top: '50%', left: '14px', transform: 'translateY(-50%)' }} />
              <input id="travel-form-destination" value={form.destination} onChange={event => updateField('destination', event.target.value)} placeholder="例：沖繩自由行、京阪神跨年" style={{ ...inputStyle, paddingLeft: '42px', borderColor: errorBorder('destination') }} />
            </div>
            <ErrorText field="destination" />
          </div>
          <div>
            <label htmlFor="travel-form-countries" style={labelStyle}>關聯國家（可多選）*</label>
            <button id="travel-form-countries" type="button" onClick={() => setShowCountryModal(true)} style={{ ...inputStyle, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '7px', minHeight: '50px', cursor: 'pointer', textAlign: 'left', borderColor: errorBorder('countries') }}>
              {form.countries.length === 0 && <span style={{ color: '#94a3b8' }}>點擊選擇國家...</span>}
              {form.countries.map(country => <span key={country} style={{ padding: '4px 9px', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '12px', fontWeight: '700' }}>{country}</span>)}
            </button>
            <ErrorText field="countries" />
          </div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-date-heading">
        <div className="gather-form-section-header">
          <h2 id="travel-date-heading">旅行日期</h2>
          <p>日期尚未確定也可以先發布，之後再從編輯頁補上。</p>
        </div>
        <div className="gather-form-fields-2">
          <div><label htmlFor="travel-form-trip_start_date" style={labelStyle}>出發日</label><input id="travel-form-trip_start_date" type="date" value={form.trip_start_date} max={form.trip_end_date || undefined} onChange={event => updateField('trip_start_date', event.target.value)} style={inputStyle} /></div>
          <div><label htmlFor="travel-form-trip_end_date" style={labelStyle}>回程日</label><input id="travel-form-trip_end_date" type="date" value={form.trip_end_date} min={form.trip_start_date || undefined} onChange={event => updateField('trip_end_date', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('trip_end_date') }} /><ErrorText field="trip_end_date" /></div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-condition-heading">
        <div className="gather-form-section-header">
          <h2 id="travel-condition-heading">人數與旅伴條件</h2>
          <p>補充預算與偏好，提高合適旅伴加入的機會。</p>
        </div>
        <div className="gather-form-fields">
          <div><span style={labelStyle}>預算等級</span><ChipGroup label="預算等級" options={TRAVEL_BUDGET_OPTIONS} selected={form.budget_range} onChange={value => updateField('budget_range', value)} /></div>
          <div className="gather-form-fields-2">
            <div><label htmlFor="travel-form-max_companions" style={labelStyle}>想找幾位旅伴</label><select id="travel-form-max_companions" value={form.max_companions} onChange={event => updateField('max_companions', Number(event.target.value))} style={inputStyle}>{[1, 2, 3, 4, 5, 6, 7, 8].map(number => <option key={number} value={number}>{number}人</option>)}</select></div>
            <div><span style={labelStyle}>旅伴性別偏好</span><ChipGroup label="旅伴性別偏好" options={TRAVEL_GENDER_OPTIONS} selected={form.gender_pref} onChange={value => updateField('gender_pref', value)} /></div>
          </div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="travel-detail-heading">
        <div className="gather-form-section-header">
          <h2 id="travel-detail-heading">詳細說明與照片</h2>
          <p>介紹旅行風格、預計行程，以及希望旅伴具備的條件。</p>
        </div>
        <div className="gather-form-fields">
          <div><label htmlFor="travel-form-description" style={labelStyle}>旅行描述</label><textarea id="travel-form-description" value={form.description} onChange={event => updateField('description', event.target.value)} placeholder={'介紹旅行風格、預計行程與希望旅伴具備的條件...\n\n例如：\nDay 1 台北出發 → 大阪逛道頓堀\nDay 2 環球影城\nDay 3 京都一日遊'} rows={8} style={{ ...inputStyle, minHeight: '164px', resize: 'vertical', lineHeight: 1.65 }} /></div>
          <div><span style={labelStyle}>旅程照片（最多5張，第一張為封面）</span><div style={{ padding: '14px', background: '#f8fafc', border: '1px dashed #bfd5e9', borderRadius: '16px' }}><TravelImagePicker items={images} onChange={setImages} /></div></div>
        </div>
      </section>

      {showCountryModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'rgba(15,23,42,0.56)' }}>
          <div role="dialog" aria-modal="true" aria-label="選擇國家" style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '430px', maxHeight: '82vh', overflow: 'hidden', background: '#ffffff', borderRadius: '20px', boxShadow: '0 24px 60px rgba(15,23,42,0.24)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 18px', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, color: '#0f172a', fontSize: '17px', fontWeight: '800' }}>選擇國家</h3>
              <button type="button" onClick={() => setShowCountryModal(false)} style={{ minHeight: '44px', padding: '7px 14px', color: '#ffffff', background: '#4e8bca', border: 'none', borderRadius: '10px', fontFamily: 'inherit', fontSize: '13px', fontWeight: '800', cursor: 'pointer' }}>完成</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
              <CountrySelector selectedCountries={form.countries} onChange={value => updateField('countries', value)} multiSelect />
            </div>
          </div>
        </div>
      )}
    </GatherFormPage>
  );
}
